// Hero: "+ Fácil." drops, "+ Potente." slams with a shake and a cloud pulse,
// "Inteligente." assembles letter by letter and then glows; the promise decodes
// itself; the live feed starts; the cloud pulses every 12 s while on the hero.
import { createFeed } from './feed'
import { $, $$, G, ST, grid, motion, odoBuild, odoRoll, rand, splitWords } from './misc'

/** Wires the hero and returns the entrance, to be played once at boot. */
export function wireHero(): (() => void) | null {
  const h1 = $('.hero .h-hero')
  if (!h1) return null
  const GRID = grid()
  const heroWords = splitWords(h1)
  const adj = (h1.dataset.adj || '').split('|')
  const br = (h1.dataset.br || '').split('|')
  heroWords.forEach((w) => {
    if (w.textContent === h1.dataset.hi) w.classList.add('hi')
    if (w.textContent === '+') w.classList.add('mas')
    if (adj.includes(w.textContent || '')) w.parentElement!.classList.add('adj')
    // One word per line: each adjective lands as its own beat.
    if (br.includes(w.textContent || '')) w.parentElement!.insertAdjacentHTML('afterend', '<br>')
  })
  const feed = createFeed()
  const heroOdos = $$('.hero-stats [data-count]').map((b) => odoBuild(b, b.dataset.count || ''))

  // The three beats: one drops, one slams, one assembles itself out of light.
  const [m1, wE, m2, wP, wMore, wSmart] = heroWords
  if (!wSmart) return null
  const wEasy = [m1, wE]; const wPower = [m2, wP]
  const smartTxt = wSmart.textContent || ''
  wSmart.classList.remove('hi')
  wSmart.innerHTML = [...smartTxt].map((c) => `<span class="ch">${c}</span>`).join('')
  const chars = $$('.ch', wSmart)
  // Each glyph carries its slice of the word gradient, so the hand-off back to one word is seamless.
  let assembled = false
  const slice = () => {
    if (assembled) return
    const W = wSmart.offsetWidth
    chars.forEach((c) => {
      c.style.backgroundImage = 'linear-gradient(100deg, #6D5CF5, #2E93F5 30%, #38BDF8 50%, #A78BFA 70%, #6D5CF5)'
      c.style.backgroundSize = `${W * 2}px 100%`; c.style.backgroundPosition = `${-c.offsetLeft}px 0`
    })
  }
  // Without the intro the entrance can start before the display font is in: slice again once it is.
  document.fonts?.ready.then(slice)

  G.set(wEasy, { yPercent: -130, scaleY: 1.5, opacity: 0, filter: 'blur(12px)', transformOrigin: '50% 100%' })
  G.set(wPower, { scale: 2.6, opacity: 0, filter: 'blur(18px)', transformOrigin: '30% 60%' })
  G.set(wMore, { x: -48, opacity: 0, filter: 'blur(10px)' })
  G.set(chars, { x: () => rand(-320, 320), y: () => rand(-220, 160), rotation: () => rand(-140, 140), scale: 0, opacity: 0, filter: 'blur(10px)' })
  const centre = (el: Element): [number, number] => { const r = el.getBoundingClientRect(); return [r.left + r.width / 2, r.top + r.height / 2] }
  const entrance = () => G.timeline({ onStart: slice })
    .to(wEasy, { yPercent: 0, scaleY: 1, opacity: 1, filter: 'blur(0px)', duration: 0.75, ease: 'expo.out' }, 0.1)
    .to(wPower, { scale: 1, opacity: 1, filter: 'blur(0px)', duration: 0.38, ease: 'power4.in', stagger: 0.06 }, 0.55)
    .to(wMore, { x: 0, opacity: 1, filter: 'blur(0px)', duration: 0.6, ease: 'expo.out' }, 1.0)
    .add(() => {
      G.fromTo('.hero-copy', { x: 0 }, { keyframes: { x: [-10, 8, -6, 4, -2, 0] }, duration: 0.42, ease: 'none' })
      motion.gl?.pulse(...centre(wP))
    }, 0.93)
    .to(chars, { x: 0, y: 0, rotation: 0, scale: 1, opacity: 1, filter: 'blur(0px)', duration: 1.15, ease: 'expo.out', stagger: { each: 0.045, from: 'center' } }, 1.05)
    .add(() => {
      assembled = true
      wSmart.textContent = smartTxt
      wSmart.classList.add('hi', 'glow')
      motion.gl?.pulse(...centre(wSmart))
    }, 2.35)
  G.set(['.pill', '.kick', '.rot', '.hero .lead', '.hero .ctas > *', '.hero-stats li'], { opacity: 0, y: 18 })

  // Leaving the hero: the feed and the copy drift away, the cloud spins into hyperspace.
  G.to('.floats', { yPercent: -35, opacity: 0, ease: 'none', scrollTrigger: { trigger: '.hero', start: 'top top', end: 'bottom top', scrub: true } })
  G.to('.hero .wrap', { yPercent: -10, opacity: 0.15, ease: 'none', scrollTrigger: { trigger: '.hero', start: 'top top', end: 'bottom top', scrub: true } })
  ST.create({ trigger: '.hero', start: 'top top', end: 'bottom top', onUpdate: (s) => motion.gl?.setHeroSpin(s.progress) })

  return function heroIn() {
    const tl = G.timeline()
    tl.to(['.pill', '.kick'], { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', stagger: 0.1 })
      .add(entrance(), 0.05)
      .to('.rot', { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' }, 0.5)
      .to('.hero .lead', { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' }, 0.6)
      .to('.hero .ctas > *', { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', stagger: 0.08 }, 0.7)
      .to('.hero-stats li', { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', stagger: 0.08 }, 0.8)
      .add(() => heroOdos.forEach((c) => odoRoll(c, 1.6)), 0.85)
      .add(() => feed?.start(), 1.1)
    wireDecode()
    // While the visitor stays on the hero, the mark sends a slow pulse through the cloud.
    setInterval(() => {
      const gl = motion.gl
      if (!gl || document.hidden || scrollY > innerHeight * 0.4) return
      const m = innerWidth < 760
      gl.pulse(m ? innerWidth / 2 : innerWidth * (0.5 + 0.26 * Math.min(1, GRID / innerWidth)), m ? innerHeight * 0.22 : innerHeight * 0.5)
    }, 12000)
  }
}

/** «Tu cartera, a otro nivel.» decodes itself character by character; several lines would rotate. */
function wireDecode(): void {
  const rw = $('.rot-w')
  if (!rw) return
  const texts = $$('span', rw).map((x) => x.textContent || '')
  if (!texts.length) return
  // The line is the hero's promise: assistive tech reads the plain copy, never the scramble.
  rw.innerHTML = `<span class="sr-only">${texts.join(' ')}</span><span aria-hidden="true">${texts[0]}</span>`
  const out = $('span[aria-hidden]', rw)!
  const glyphs = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789#%&*+'
  let i = 0
  const decode = (to: string) => {
    const from = out.textContent || ''; const o = { p: 0 }
    G.to(o, {
      p: 1, duration: 0.9, ease: 'power2.inOut',
      onUpdate: () => {
        const len = Math.round(from.length + (to.length - from.length) * o.p)
        const fixed = Math.floor(to.length * o.p)
        let txt = to.slice(0, fixed)
        for (let k = fixed; k < len; k++) txt += to[k] === ' ' ? ' ' : glyphs[Math.floor(Math.random() * glyphs.length)]
        out.textContent = txt
      },
      onComplete: () => { out.textContent = to },
    })
  }
  if (texts.length > 1) setInterval(() => { i = (i + 1) % texts.length; decode(texts[i]) }, 2800)
  else { out.textContent = ''; G.delayedCall(2.4, () => decode(texts[0])) }
}
